import { defineStore } from 'pinia'
import type { ImpersonatePayload, ItemAvailabilityParams } from '~/types/agent'
import { ImpersonationRefreshError } from '~/types/agent'
import type { TableQueryPayload } from '~/types/demo'
import { filterTableUrlParams, getLanguagePath, getStampinupApiDomain, processQueryFilter } from '~/utils'
import type { User, OrderDemoHistoryResponse } from '~/types/account'
import { UserRole } from '~/types/account'
import { Culture } from '~/types/locale'
import { usePersistentStore } from '~/stores/persistent'
import { useCartStore } from '~/stores/cart'
import { useAccountStore } from '~/stores/account'
import { useLocaleStore } from '~/stores/locale'
import { useRootStore } from '~/stores/root'
import { useTypedRuntimeConfig } from '~/utils/runtimeConfig'

export const useAgentStore = defineStore('agent', () => {
  const { $api: api } = useNuxtApp()
  const i18n = useI18n()
  const { userRole, setUser, user, isGuest } = useSAuth()

  const persistentStore = usePersistentStore()
  const cartStore = useCartStore()
  const accountStore = useAccountStore()
  const localeStore = useLocaleStore()
  const rootStore = useRootStore()

  const showImpersonationHeader = ref(false)

  const persistedViewAsDate = computed(() => persistentStore.getPersistentValue('viewAsDate'))
  // Read through the store on each evaluation: setImpersonationContext REPLACES
  // the context object, so capturing it in a const freezes isImpersonating at its
  // initial value (stale across impersonation). Matches nuxt2 stores/agent.ts.
  const isImpersonating = computed(() => persistentStore.impersonationContext.isImpersonating)
  const noTranslate = computed(() => persistentStore.getPersistentValue('noTranslate'))
  const demoOrders = ref<OrderDemoHistoryResponse | null>(null)

  // Surfaced during impersonation too, not just when browsing as the agent: an
  // agent testing future promotions impersonates the customer and shifts the
  // date, so suppressing it here would price the impersonated cart at today and
  // make the date-shifted guards (AgentHeader's indicator, OrderSummaryButton's
  // blockForDateShiftedTest) unsatisfiable, since both read isImpersonating AND
  // viewAsDate. Ports nuxt2 PR 22432 (AB#60489), which removed the same
  // exclusion; the port carried its consumers over but not this.
  const viewAsDate = computed(() => persistedViewAsDate.value)

  const updateViewAsDate = (viewAsDate: string | null) => {
    persistentStore.updateValue('viewAsDate', viewAsDate)
  }
  // Storefront home URL for the current culture, carrying the language segment
  // (/fr, /de) rather than the culture — the culture form (e.g. stampinup.ca/fr-ca)
  // 404s, so an origin-only URL would silently drop a French agent onto the
  // English site. See getLanguagePath / LanguagePath.
  const localeHome = () =>
    `${window.location.origin}${getLanguagePath(localeStore.culture) ?? ''}/`

  // Start the agent Auth Code + PKCE sign-in by handing the browser off to the
  // backend's oidc-start endpoint, which redirects to Microsoft.
  //
  // This is a deliberate top-level navigation, NOT an $api/$fetch call: XHR
  // cannot follow the cross-origin redirect to Microsoft. Because $api is
  // bypassed, the API origin and the {culture} path segment (normally injected
  // by $api's onRequest hook) are built here by hand, mirroring api.ts.
  //
  // mode travels in the query string: mode=agent produces Role=Agent (agent
  // tooling, but CheckoutController blocks it at payment); mode=customer
  // produces Role=Customer so an employee can check out with their staff
  // discount. The backend OIDC callback can't infer intent, so it must be sent.
  const startOidcSignIn = (mode: 'agent' | 'customer' = 'agent') => {
    const $config = useTypedRuntimeConfig()
    const culture = localeStore.culture
    const apiBase = getStampinupApiDomain($config.public.API_PREFIX, culture, $config.public.API_PORT)
    const returnUrl = encodeURIComponent(localeHome())
    window.location.assign(`${apiBase}/${culture}/agent/oidc-start?mode=${mode}&returnUrl=${returnUrl}`)
  }

  // Sign the agent out of the backend-held OIDC session.
  //
  // Sign-out is a POST, not a top-level GET: it is destructive and the session
  // cookie is SameSite=None, so the backend requires POST + a same-origin check
  // ($api sends credentials and the Origin header). The backend returns 200
  // with the storefront URL in the body rather than a 302 — a cross-origin 302
  // would be followed by fetch and rejected by CORS on the storefront origin,
  // leaving the page looking signed-in — so we navigate to the returned URL.
  // returnUrl carries the language segment so the backend doesn't fall back to
  // its default (a single .com with the culture interpolated → wrong domain).
  const signOutOidc = async () => {
    const response = await api<{ redirectUrl?: string }>('agent/sign-out', {
      method: 'post',
      query: { returnUrl: localeHome() }
    })
    window.location.replace(response?.redirectUrl || localeHome())
  }
  const clearImpersonation = async () => {
    // Set once the session is the agent's again. A failure after this point is only a stale
    // refresh, reported as ImpersonationRefreshError so a caller can tell it apart. AB#64477
    let restored = false
    try {
      rootStore.rootLoading = true

      // necessary to update the cookie values in the persistent store as the api returned new cookies
      await api('agent/clear-impersonation', { method: 'post' })
      persistentStore.updateStateFromServerCookies()
      persistentStore.clearImpersonationContext()
      updateViewAsDate(null)

      // Clear only impersonation-related cookies, keep agentEmail for future impersonation
      useCookie('isAgentImpersonated', { secure: true, httpOnly: false }).value = null
      restored = true

      // resets the user to empty
      const user = await api<User>('account')
      setUser(user)
      // TODO: research why this does not work in incognito
      // await $auth.fetchUser()

      await Promise.all([
        cartStore.fetchCart(),
        rootStore.fetchCategories()
      ])
    } catch (error) {
      if (restored) {
        throw new ImpersonationRefreshError({ cause: error })
      }
      throw error
    } finally {
      rootStore.rootLoading = false
    }
  }
  const impersonate = async (email: ImpersonatePayload) => {
    let isSameDomain = true
    try {
      rootStore.rootLoading = true
      // if the account is only a customer account the B/E does not send the isDemo cookie on that request
      // so always set it to false and let the endpoint update it only if the cookie is set in the api response
      persistentStore.updateValue('isDemo', false)

      // necessary to update the cookie values in the persistent store as the api returned new cookies
      const { culture } = await api<{ culture: string }>('agent/impersonate', { method: 'post', body: email })
      persistentStore.updateStateFromServerCookies()
      persistentStore.startImpersonation({
        impersonatedEmail: email.email,
        impersonatedRole: userRole.value,
      })

      const rawDemoCulture = culture ?? user.value.culture
      const isCulture = (v: string): v is Culture => Object.values(Culture).includes(v as Culture)
      const demoCulture: Culture = isCulture(rawDemoCulture) ? rawDemoCulture : Culture.EN_US
      isSameDomain = localeStore.culture === demoCulture
      if (isSameDomain) {
        const user = await api<User>('account')
        setUser(user)
        // TODO: research why this does not work in incognito
        // await $auth.fetchUser()

        await Promise.all([
          cartStore.fetchCart(),
          rootStore.fetchCategories()
        ])
        if (!isGuest.value) {
          await accountStore.fetchLoyaltyInventory()
        }
      } else {
        await localeStore.changeAuthenticatedLocale({ culture: demoCulture })
        return false
      }
      return isSameDomain
    } finally {
      if (isSameDomain) {
        rootStore.rootLoading = false
      }
    }
  }
  const changeWebsiteRole = async (role: UserRole) => {
    await api(`agent/view?role=${role}`, { method: 'post' })
    persistentStore.updateValue('isDemo', role === UserRole.Demonstrator)

    await Promise.all([
      cartStore.fetchCart(),
      rootStore.fetchCategories()
    ])
  }
  const untranslate = async () => {
    persistentStore.updateValue('noTranslate', !noTranslate.value)
    const $config = useTypedRuntimeConfig()
    if (noTranslate.value) {
      // with no fallback set and unsupported language keys show
      const { $clearCache } = useNuxtApp()
      $clearCache()
    } else {
      // current culture translations still loaded so just put back
      await i18n.$switchLocale(localeStore.culture)
      $config.public.i18nConfig.fallbackLocale = 'en-gb'
    }
  }
  const viewItemAvailability = async (formData: ItemAvailabilityParams) => {
    let fileName = 'item_availability'
    const urlParams = new URLSearchParams()
    urlParams.append('audience', formData.audience)
    fileName += `_${formData.audience}`
    if (formData.viewAsDate) {
      urlParams.append('viewAsDate', formData.viewAsDate)
      fileName += `_${formData.viewAsDate.slice(0, 10)}`
    }
    if (formData.culture) {
      urlParams.append('itemCulture', formData.culture)
      fileName += `_${formData.culture}`
    }
    if (formData.excludeFrom && formData.excludeFrom !== '') {
      urlParams.append('excludeFrom', formData.excludeFrom)
      fileName += `_${formData.excludeFrom}`
    }
    if (formData.exclusiveTo && formData.exclusiveTo !== '') {
      urlParams.append('exclusiveTo', formData.exclusiveTo)
      fileName += `_${formData.exclusiveTo}`
    }
    if (formData.lifeCycleState && formData.lifeCycleState !== '') {
      urlParams.append('lifeCycleState', formData.lifeCycleState)
      fileName += `_${formData.lifeCycleState}`
    }
    if (formData.category && formData.category !== '') {
      urlParams.append('category', formData.category)
      fileName += `_${formData.category}`
    }
    if (formData.itemIds) {
      formData.itemIds.split(',').forEach((itemId) => {
        urlParams.append('itemId', itemId)
      })
    }
    fileName += '.csv'
    await downloadBlob(`agent/itemavailability/csv?${urlParams.toString().replace('+', '%20')}`, fileName)
  }
  const fetchDemoOrders = async (query: TableQueryPayload) => {
    const params = filterTableUrlParams(processQueryFilter(query))
    if (query.term) {
      demoOrders.value = await api<OrderDemoHistoryResponse>('/agent/orders/filter', { query: { ...params } })
    } else {
      demoOrders.value = null
    }
  }

  const checkImpersonationAuthorization = async () => {
    const response = await api.raw('agent/authorize', { method: 'GET' })
    showImpersonationHeader.value = response.status === 200
  }

  return {
    viewAsDate,
    isImpersonating,
    noTranslate,
    updateViewAsDate,
    startOidcSignIn,
    signOutOidc,
    demoOrders,
    clearImpersonation,
    impersonate,
    changeWebsiteRole,
    untranslate,
    viewItemAvailability,
    fetchDemoOrders,
    showImpersonationHeader,
    checkImpersonationAuthorization
  }
})
